import * as XLSX from "xlsx";
import { SpecError } from "../errors";
import { INPUT_LIMITS } from "../limits";
import type { RawRow } from "../types";

/**
 * Phase 1: Excel バイナリを「シート名 → 行配列の Map」に変換する。
 *
 * セル値は文字列 / null に正規化。入力サイズ上限超過は `INPUT_TOO_LARGE`、
 * xlsx として読めない場合は `INVALID_FILE_FORMAT` を throw。
 */
export class ExcelReader {
  read(input: ArrayBuffer | Uint8Array): Map<string, RawRow[]> {
    const data = input instanceof Uint8Array ? input : new Uint8Array(input);

    if (data.length > INPUT_LIMITS.maxFileBytes) {
      throw new SpecError(
        "INPUT_TOO_LARGE",
        `Input file size ${data.length} bytes exceeds the limit of ${INPUT_LIMITS.maxFileBytes} bytes.`,
        { kind: "file_size", actual: data.length, limit: INPUT_LIMITS.maxFileBytes },
      );
    }

    // xlsx は zip 形式なので先頭 4 バイトが PK\x03\x04。SheetJS は壊れた入力を prn として読んでしまうため事前に弾く。
    if (
      data.length < 4 ||
      data[0] !== 0x50 || data[1] !== 0x4b ||
      data[2] !== 0x03 || data[3] !== 0x04
    ) {
      throw new SpecError(
        "INVALID_FILE_FORMAT",
        "Input is not a valid .xlsx file (ZIP magic bytes not found).",
      );
    }

    let workbook: XLSX.WorkBook;
    try {
      workbook = XLSX.read(data, { type: "array", cellDates: true });
    } catch (cause) {
      throw new SpecError(
        "INVALID_FILE_FORMAT",
        "Failed to parse input as .xlsx.",
        undefined,
        cause,
      );
    }

    if (workbook.SheetNames.length === 0) {
      throw new SpecError(
        "INVALID_FILE_FORMAT",
        "Workbook contains no sheets.",
      );
    }

    if (workbook.SheetNames.length > INPUT_LIMITS.maxSheetCount) {
      throw new SpecError(
        "INPUT_TOO_LARGE",
        `Sheet count ${workbook.SheetNames.length} exceeds the limit of ${INPUT_LIMITS.maxSheetCount}.`,
        { kind: "sheet_count", actual: workbook.SheetNames.length, limit: INPUT_LIMITS.maxSheetCount },
      );
    }

    const result = new Map<string, RawRow[]>();
    for (const name of workbook.SheetNames) {
      const sheet = workbook.Sheets[name];
      // 行数上限は実セルの占有レンジで「マテリアライズ前」に検査し、!ref をそのレンジへ clamp する。
      // LibreOffice は中身が数行でも !ref をシート全域 (1,048,576 行) で書き出すため、宣言レンジで判定すると
      // 正常な仕様書まで弾いてしまう。また sheet_to_json は blankrows:true で !ref 全域を配列化するため、
      // clamp しないと sparse な巨大 !ref (実データ数行) でも OOM する。占有レンジに揃えることで両方を防ぐ。
      const ref = sheet["!ref"];
      if (ref) {
        const declared = XLSX.utils.decode_range(ref);
        const used = this.usedExtent(sheet);
        if (!used) {
          // 実セルが 1 つも無い (全域 !ref だけの) シートは空シート扱い (全域を materialize しない)。
          result.set(name, []);
          continue;
        }
        const rowCount = used.maxRow - declared.s.r + 1;
        if (rowCount > INPUT_LIMITS.maxRowsPerSheet) {
          throw new SpecError(
            "INPUT_TOO_LARGE",
            `Sheet "${name}" contains data spanning ${rowCount} rows, exceeding the limit of ${INPUT_LIMITS.maxRowsPerSheet}.`,
            { kind: "row_count", sheetName: name, actual: rowCount, limit: INPUT_LIMITS.maxRowsPerSheet },
          );
        }
        sheet["!ref"] = XLSX.utils.encode_range({
          s: declared.s,
          e: { r: used.maxRow, c: used.maxCol },
        });
      }
      const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: null,
        raw: true,
        blankrows: true,
      });
      result.set(name, rows.map((row) => this.normalizeRow(row, name)));
    }
    return result;
  }

  /**
   * 実セルが存在する占有レンジの末尾 (最大行・最大列、0 始まり) を返す。実セルが無ければ null。
   * `!ref` の宣言レンジが過大 (LibreOffice の全域指定等) でも、実データの広がりだけを測る。
   */
  private usedExtent(sheet: XLSX.WorkSheet): { maxRow: number; maxCol: number } | null {
    let maxRow = -1;
    let maxCol = -1;
    for (const key of Object.keys(sheet)) {
      if (key[0] === "!") continue; // !ref / !cols / !merges 等のメタキー
      const { r, c } = XLSX.utils.decode_cell(key);
      if (r > maxRow) maxRow = r;
      if (c > maxCol) maxCol = c;
    }
    return maxRow < 0 ? null : { maxRow, maxCol };
  }

  private normalizeRow(row: unknown[], sheetName: string): RawRow {
    return row.map((cell) => {
      if (cell === null || cell === undefined) return null;
      let s: string;
      if (typeof cell === "string") s = cell;
      else if (typeof cell === "number") s = String(cell);
      else if (typeof cell === "boolean") s = cell ? "TRUE" : "FALSE";
      else if (cell instanceof Date) s = cell.toISOString();
      else s = String(cell);
      if (s.length > INPUT_LIMITS.maxCellLength) {
        throw new SpecError(
          "INPUT_TOO_LARGE",
          `Cell value length ${s.length} in sheet "${sheetName}" exceeds the limit of ${INPUT_LIMITS.maxCellLength} characters.`,
          { kind: "cell_length", sheetName, actual: s.length, limit: INPUT_LIMITS.maxCellLength },
        );
      }
      return s;
    });
  }
}
